import pdfjsLib from './pdfWorker.js';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';
import { showModalDialog } from './ShowModalDialog.jsx';

/**
 * Builds page thumbnails from a PDF ArrayBuffer.
 * Loads the PDF document, renders small and large thumbnails for each page,
 * clamps the scale to avoid huge canvases, and reports progress via callback.
 * @param {ArrayBuffer} arrayBuffer - The PDF data.
 * @param {function} onProgress - Optional callback for progress percentage.
 * @returns {Promise<{pages: Array}>} - Array of page info with thumbnails.
 */
function cloneForPdfjs(data) {
  if (!data) return data;
  if (ArrayBuffer.isView(data)) {
    // Clone just the viewed range to a fresh Uint8Array
    return new Uint8Array(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength));
  }
  if (data instanceof ArrayBuffer) {
    return data.slice(0);
  }
  return data;
}

export async function buildPagesFromPdf(arrayBuffer, onProgress = () => { }) {
  // Load the PDF document using pdfjsLib
  const data = cloneForPdfjs(arrayBuffer);
  const doc = await pdfjsLib.getDocument({ data }).promise;
  const pages = [];

  // Define scales for rendering thumbnails
  const smallScale = 0.35; // scale for small thumbnails (compact/thumb lists)
  const maxViewerWidth = 1400; // max width for large thumbnails to avoid huge canvases
  const maxViewerScale = 1.6; // upper bound for large viewer scale

  // Iterate through each page of the PDF
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);

    // Render small thumbnail (fast rendering)
    const vpSmall = page.getViewport({ scale: smallScale });
    const canvasSmall = document.createElement('canvas');
    const ctxSmall = canvasSmall.getContext('2d');
    canvasSmall.width = Math.ceil(vpSmall.width);
    canvasSmall.height = Math.ceil(vpSmall.height);
    await page.render({ canvasContext: ctxSmall, viewport: vpSmall }).promise;
    const thumb = canvasSmall.toDataURL('image/webp', 0.8);

    // Calculate a larger scale based on page's base size, clamped to maxViewerScale and maxViewerWidth
    const baseViewport = page.getViewport({ scale: 1 });
    const computedScale = Math.min(maxViewerScale, maxViewerWidth / baseViewport.width);
    // Ensure the viewer scale is at least a minimum value and not smaller than smallScale
    const viewerScale = Math.max(computedScale, Math.max(smallScale, 0.85));

    // Render large thumbnail for gallery viewer
    const vpLarge = page.getViewport({ scale: viewerScale });
    const canvasLarge = document.createElement('canvas');
    const ctxLarge = canvasLarge.getContext('2d');
    canvasLarge.width = Math.ceil(vpLarge.width);
    canvasLarge.height = Math.ceil(vpLarge.height);
    await page.render({ canvasContext: ctxLarge, viewport: vpLarge }).promise;
    const largeThumb = canvasLarge.toDataURL('image/webp', 0.92);

    // Store page info with thumbnails and unique id
    pages.push({
      id: crypto.randomUUID(),
      pageNumber: i,
      name: `page${i}.pdf`,
      thumb, // small thumbnail
      largeThumb, // large thumbnail for viewer
    });

    // Report progress percentage to callback, ignoring callback errors
    try { onProgress(Math.round((i / doc.numPages) * 100)); } catch (e) { /* ignore callback errors */ }
  }

  // Final progress report at 100%
  try { onProgress(100); } catch (e) { /* ignore */ }
  return { pages };
}

/**
 * Splits a single page from the original PDF bytes and returns it as a Blob.
 * Loads the PDF, copies the specified page, and handles encryption errors by showing a modal.
 * @param {Uint8Array} originalBytes - The original PDF data.
 * @param {number} pageNumber - The page number to extract (1-based).
 * @returns {Promise<Blob>} - Blob containing the single page PDF.
 * @throws Will throw if the PDF is encrypted or another error occurs.
 */
export async function splitPageBytes(originalBytes, pageNumber) {
  try {
    // Load the PDF document (do not ignore encryption)
    const src = await PDFDocument.load(originalBytes);
    // Create a new PDF document to hold the single page
    const out = await PDFDocument.create();
    // Copy the specified page from source PDF to new PDF
    const [p] = await out.copyPages(src, [pageNumber - 1]);
    out.addPage(p);
    // Save the new PDF as bytes
    const bytes = await out.save();
    // Return as a Blob with PDF MIME type
    return new Blob([bytes], { type: 'application/pdf' });
  } catch (err) {
    // If the error indicates encryption, show a warning modal dialog
    if (err && /encrypted/i.test(err.message || '')) {
      await showModalDialog({
        title: 'Encrypted PDF',
        body: 'This PDF is encrypted and cannot be processed. If you want to proceed anyway, remove the encryption and try again.',
        type: 'warning',
        buttonType: 'ok',
      });
      // Stop further processing by throwing a specific error
      throw new Error('ENCRYPTED_DOCUMENT');
    }
    // Re-throw any other errors
    throw err;
  }
}

/**
 * Splits a single page using a dedicated web worker for off-main-thread processing.
 * Communicates with the worker via postMessage and listens for the response.
 * Handles success and error cases accordingly.
 * @param {Uint8Array} originalBytes - The original PDF data.
 * @param {number} pageNumber - The page number to extract (1-based).
 * @returns {Promise<Blob>} - Blob containing the single page PDF.
 */
export function createPdfWorker() {
  if (typeof window === 'undefined' || typeof Worker === 'undefined') return null;
  try {
    return new Worker(new URL('./pdfCustomWorker.js', import.meta.url), { type: 'classic' });
  } catch (_) {
    return null;
  }
}

export async function splitPageBytesWorker(originalBytes, pageNumber) {
  return new Promise((resolve, reject) => {
    // Create a worker lazily and in a guarded way for test environments
    const worker = createPdfWorker();
    if (!worker) {
      reject(new Error('Worker not available'));
      return;
    }
    // Listen for messages from the worker
    worker.onmessage = (e) => {
      const { success, bytes, error } = e.data;
      // Terminate the worker after receiving a message
      worker.terminate();
      if (success) {
        // On success, resolve with a Blob of the split page
        resolve(new Blob([bytes], { type: 'application/pdf' }));
      } else {
        // On failure, reject with an error message
        reject(new Error(error));
      }
    };
    // Post a message to the worker to start splitting the page
    worker.postMessage({ type: 'splitPage', originalBytes, pageNumber });
  });
}

/**
 * Saves a single page by splitting it and triggering a download.
 * Calls splitPageBytes and uses triggerDownload to save the file.
 * Handles encryption errors gracefully.
 * @param {Uint8Array} uint8array - The original PDF data.
 * @param {Object} page - Page info object with pageNumber and name.
 */
export async function saveSinglePage(uint8array, page) {
  try {
    // Split the specified page into a Blob
    const blob = await splitPageBytes(uint8array, page.pageNumber);
    // Trigger file download for the single page PDF
    triggerDownload(blob, page.name);
  } catch (err) {
    // If the document is encrypted, user has been informed; stop silently
    if (err.message === 'ENCRYPTED_DOCUMENT') return;
    // Re-throw other errors
    throw err;
  }
}

/**
 * Saves multiple pages as a ZIP archive.
 * Iterates over pages, splits each page, adds to ZIP, reports progress,
 * and triggers download of the final ZIP file.
 * @param {Uint8Array} uint8array - The original PDF data.
 * @param {Array} pages - Array of page info objects.
 * @param {function} onProgress - Optional callback for progress percentage.
 */
export async function saveAsZip(uint8array, pages, onProgress = () => { }) {
  const zip = new JSZip();
  try {
    // Iterate over each page to split and add to ZIP
    for (let i = 0; i < pages.length; i++) {
      const p = pages[i];
      // Split the page into a Blob
      const blob = await splitPageBytes(uint8array, p.pageNumber);
      // Convert Blob to ArrayBuffer for JSZip
      const arrayBuf = await blob.arrayBuffer();
      // Add the page PDF to the ZIP archive with a safe filename
      zip.file(safeName(p.name), arrayBuf);
      // Report progress percentage to callback, ignoring callback errors
      try { onProgress(Math.round(((i + 1) / pages.length) * 100)); } catch (e) { /* ignore */ }
    }
    // Generate the ZIP archive as a Blob
    const content = await zip.generateAsync({ type: 'blob' });
    // Trigger download of the ZIP file
    triggerDownload(content, 'pages.zip');
    // Final progress report at 100%
    try { onProgress(100); } catch (e) { /* ignore */ }
  } catch (err) {
    // If encrypted, user has been informed; stop silently
    if (err.message === 'ENCRYPTED_DOCUMENT') return;
    // Re-throw other errors
    throw err;
  }
}

/**
 * Triggers a file download in the browser by creating an object URL,
 * creating an anchor element, clicking it, and cleaning up resources.
 * @param {Blob} blob - The file data as a Blob.
 * @param {string} filename - The name for the downloaded file.
 */
function triggerDownload(blob, filename) {
  // Create an object URL for the Blob
  const url = URL.createObjectURL(blob);
  // Create a temporary anchor element
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName(filename);
  // Append anchor to document body to make it clickable
  document.body.appendChild(a);
  // Trigger click to start download
  a.click();
  // Remove the anchor element from the DOM
  a.remove();
  // Revoke the object URL to free memory
  URL.revokeObjectURL(url);
}

/**
 * Sanitizes a filename by replacing characters that are not alphanumeric,
 * dot, underscore, or hyphen with underscores.
 * This ensures safe filenames for downloads and file systems.
 * @param {string} name - The original filename.
 * @returns {string} - The sanitized filename.
 */
function safeName(name) {
  return name.replace(/[^a-z0-9._-]+/gi, '_');
}

/**
 * Converts a data URL (e.g., from canvas.toDataURL) to a Blob.
 * Supports base64-encoded data URLs like "data:image/webp;base64,...".
 * @param {string} dataUrl - The data URL to convert.
 * @returns {Blob}
 */
function dataUrlToBlob(dataUrl) {
  // Split the data URL into metadata and data parts
  const [meta, data] = dataUrl.split(',');
  // Extract MIME type from the metadata header
  const mimeMatch = meta.match(/data:([^;]+);/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  // Decode base64 payload
  const binary = atob(data);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Convert an image data URL (any supported format) to a PNG Blob by drawing to a canvas.
 * @param {string} dataUrl
 * @returns {Promise<Blob>} resolves to a PNG Blob
 */
function dataUrlToPngBlob(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error('PNG conversion failed'));
          resolve(blob);
        }, 'image/png');
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image for PNG conversion'));
    img.src = dataUrl;
  });
}

/**
 * Downloads the large thumbnail image for a given page as a PNG.
 * Uses the page's `largeThumb` data URL and a sensible default filename.
 * @param {{ name: string, pageNumber: number, largeThumb: string }} page
 */
export async function saveLargeThumb(page) {
  if (!page || !page.largeThumb) return;
  const pngBlob = await dataUrlToPngBlob(page.largeThumb);
  const base = (page.name || `page${page.pageNumber}`)
    .replace(/\.[^.]+$/, '')
    .trim();
  const filename = safeName(`${base}-preview.png`);
  triggerDownload(pngBlob, filename);
}

/**
 * Downloads the small thumbnail image for a given page as a PNG.
 * @param {{ name: string, pageNumber: number, thumb: string }} page
 */
export async function saveSmallThumb(page) {
  if (!page || !page.thumb) return;
  const pngBlob = await dataUrlToPngBlob(page.thumb);
  const base = (page.name || `page${page.pageNumber}`)
    .replace(/\.[^.]+$/, '')
    .trim();
  const filename = safeName(`${base}-thumb.png`);
  triggerDownload(pngBlob, filename);
}

/**
 * Render and download a higher-resolution PNG of the given page directly from the PDF.
 * @param {Uint8Array|ArrayBuffer} originalBytes - Original PDF bytes
 * @param {{ pageNumber: number, name?: string }} page - Page info
 * @param {{ maxWidth?: number, maxScale?: number, minScale?: number }} [opts]
 */
export async function saveLargeThumbHiRes(originalBytes, page, opts = {}) {
  const { maxWidth = 2400, maxScale = 3, minScale = 1.25 } = opts;
  if (!originalBytes || !page || !page.pageNumber) return;

  // Load doc and page
  const data = cloneForPdfjs(originalBytes);
  const doc = await pdfjsLib.getDocument({ data }).promise;
  const pdfPage = await doc.getPage(page.pageNumber);

  // Compute scale: clamp by maxWidth and maxScale, ensure minScale
  const baseViewport = pdfPage.getViewport({ scale: 1 });
  const widthLimitedScale = maxWidth / baseViewport.width;
  const scale = Math.max(minScale, Math.min(maxScale, widthLimitedScale));

  // Render to canvas
  const viewport = pdfPage.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  await pdfPage.render({ canvasContext: ctx, viewport }).promise;

  // Export PNG
  const pngBlob = await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('PNG export failed')), 'image/png');
  });

  const base = (page.name || `page${page.pageNumber}`)
    .replace(/\.[^.]+$/, '')
    .trim();
  const filename = safeName(`${base}-preview-hires.png`);
  triggerDownload(pngBlob, filename);
}

/**
 * Saves selected pages as a single combined PDF.
 * Loads the original PDF, creates a new PDF, copies selected pages,
 * reports progress, handles encryption errors, and triggers download.
 * @param {Uint8Array} originalBytes - The original PDF data.
 * @param {Array} pages - Array of page info objects to include.
 * @param {function} onProgress - Optional callback for progress percentage.
 */
export async function saveAsSinglePdf(originalBytes, pages, onProgress = () => { }) {
  try {
    // Load the original PDF document (do not ignore encryption)
    const src = await PDFDocument.load(originalBytes);
    // Create a new PDF document to hold selected pages
    const out = await PDFDocument.create();

    // Iterate over selected pages to copy into new PDF
    for (let i = 0; i < pages.length; i++) {
      const p = pages[i];
      // Copy the specified page from source PDF
      const [copied] = await out.copyPages(src, [p.pageNumber - 1]);
      out.addPage(copied);
      // Report progress percentage to callback, ignoring callback errors
      try { onProgress(Math.round(((i + 1) / pages.length) * 100)); } catch (e) { /* ignore */ }
    }

    // Save the combined PDF as bytes
    const bytes = await out.save();
    // Trigger download of the combined PDF as 'document.pdf'
    triggerDownload(new Blob([bytes], { type: 'application/pdf' }), 'document.pdf');
    // Final progress report at 100%
    try { onProgress(100); } catch (e) { /* ignore */ }
  } catch (err) {
    // If the error indicates an encrypted document, show a warning modal
    if (err && (/encrypted/i.test(err.message || '') || err.message === 'ENCRYPTED_DOCUMENT')) {
      await showModalDialog({
        title: 'Encrypted PDF',
        body: 'This PDF is encrypted and cannot be processed. If you want to proceed anyway, remove the encryption and try again.',
        type: 'warning',
        buttonType: 'ok',
      });
      return;
    }
    // Re-throw other errors
    throw err;
  }
}

/**
 * Saves multiple pages as a ZIP archive of PNG images.
 * Renders each selected page to PNG (hi-res by default), reports progress, and downloads a ZIP.
 * @param {Uint8Array|ArrayBuffer} originalBytes - The original PDF bytes
 * @param {Array<{name:string,pageNumber:number}>} pages - Selected pages
 * @param {function} onProgress - Optional progress callback (0-100)
 * @param {{ maxWidth?: number, maxScale?: number, minScale?: number }} [opts]
 */
export async function saveAsZipPng(originalBytes, pages, onProgress = () => {}, opts = {}) {
  const { maxWidth = 2400, maxScale = 3, minScale = 1.25 } = opts;
  const zip = new JSZip();
  try {
    const data = cloneForPdfjs(originalBytes);
    const doc = await pdfjsLib.getDocument({ data }).promise;

    for (let i = 0; i < pages.length; i++) {
      const p = pages[i];
      const pdfPage = await doc.getPage(p.pageNumber);

      // Compute scale similar to saveLargeThumbHiRes
      const baseViewport = pdfPage.getViewport({ scale: 1 });
      const widthLimitedScale = maxWidth / baseViewport.width;
      const scale = Math.max(minScale, Math.min(maxScale, widthLimitedScale));
      const viewport = pdfPage.getViewport({ scale });

      // Render to canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await pdfPage.render({ canvasContext: ctx, viewport }).promise;

      // Canvas to PNG Blob
      const pngBlob = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('PNG export failed')), 'image/png');
      });
      const arrayBuf = await pngBlob.arrayBuffer();

      // Filename: use page.name or fallback to pageX.png
      const base = (p.name || `page${p.pageNumber}`).replace(/\.[^.]+$/, '').trim();
      const fname = safeName(`${base}.png`);
      zip.file(fname, arrayBuf);

      try { onProgress(Math.round(((i + 1) / pages.length) * 100)); } catch (_) {}
    }

    const content = await zip.generateAsync({ type: 'blob' });
    triggerDownload(content, 'pages_png.zip');
    try { onProgress(100); } catch (_) {}
  } catch (err) {
    if (err && /encrypted/i.test(err.message || '')) {
      await showModalDialog({
        title: 'Encrypted PDF',
        body: 'This PDF is encrypted and cannot be processed. If you want to proceed anyway, remove the encryption and try again.',
        type: 'warning',
        buttonType: 'ok',
      });
      return;
    }
    throw err;
  }
}
